/**
 * code store - 공통코드(FRM_CODE) 캐시 store.
 * fetchCode 가 codeKindMap(resolveCdKind)으로 실 CD_KIND 를 얻어 GET /api/v1/codes/{CD_KIND} 를 호출한다.
 * 서버 실패·0건·매핑 없음이면 빈 목록이다 - 프론트 가짜 라벨로 채우지 않는다(서버 장애·코드 미적재를 가리지 않게).
 */
import { defineStore } from 'pinia';
import http from '@/services/http';
import { resolveCdKind } from '@/constants/codeKindMap';

export const useCodeStore = defineStore('code', {
  state: () => ({
    codesByKind: {}, // { EMP_KIND: [{cd,cd_nm}], ... }
  }),
  getters: {
    getCodeName: (s) => (cdKind, cd, opt = {}) => {
      const list = s.codesByKind[cdKind] || [];
      const hit = list.find((x) => x.cd === cd && (!opt.parentCd || x.mainCd === opt.parentCd));
      return hit ? hit.cd_nm : '';
    },
    /**
     * 드롭다운용 옵션 [{value,label}]. 아직 불러오지 않은 종류는 빈 목록이다.
     * opt.withAll 을 주면 '전체' 행을 앞에 붙인다.
     */
    options: (s) => (cdKind, opt = {}) => {
      const codes = s.codesByKind[cdKind] || [];
      const filtered = opt.parentCd
        ? codes.filter((x) => x.mainCd === opt.parentCd)
        : codes;
      const mapped = filtered.map((x) => ({ value: x.cd, label: x.cd_nm }));
      return opt.withAll ? [{ value: '', label: opt.allLabel || '전체' }, ...mapped] : mapped;
    },
  },
  actions: {
    setCodes(cdKind, list) {
      this.codesByKind = { ...this.codesByKind, [cdKind]: list || [] };
    },
    /**
     * 코드 1종 로드(캐시). 응답 {success,data:[{cd,cdNm,...}]} -> store 표준형 {cd,cd_nm}.
     * force 면 캐시가 있어도 다시 읽는다(관리자가 이름을 바꾸는 코드를 화면에 들어올 때마다 새로 보이게). 실패하면 앞 목록을 둔다.
     */
    async fetchCode(frontKind, { force = false } = {}) {
      if (!force && this.codesByKind[frontKind]) return this.codesByKind[frontKind];

      const realKind = resolveCdKind(frontKind);
      if (!realKind) {
        console.warn(`[code] 코드종류 매핑 없음: ${frontKind} (constants/codeKindMap.js)`);
        return [];
      }
      try {
        const body = await http.get(`/api/v1/codes/${realKind}`);
        const list = (body?.data || []).map((x) => ({ cd: x.cd, cd_nm: x.cdNm, mainCd: x.mainCd }));
        this.setCodes(frontKind, list);
        return list;
      } catch (e) {
        // 실패는 캐시하지 않는다 - 다음 화면 진입에서 다시 조회한다.
        console.warn(`[code] 공통코드 조회 실패: ${realKind}`, e);
        return this.codesByKind[frontKind] || [];
      }
    },
    /**
     * 실 코드종류(FRM_CODE.CD_KIND)로 해석되는 화면 키의 캐시를 지운다. 인자가 없으면 전부 비운다.
     * 코드를 추가·삭제·이름 변경하는 저장 뒤와 로그아웃 때 부른다 - 다음 fetchCode 가 서버를 다시 읽는다.
     */
    invalidate(...realKinds) {
      if (!realKinds.length) { this.codesByKind = {}; return; }
      const drop = new Set(realKinds);
      this.codesByKind = Object.fromEntries(Object.entries(this.codesByKind).filter(([k]) => !drop.has(resolveCdKind(k))));
    },
    /** 여러 종을 한 번에 로드. */
    async fetchCodes(frontKinds = []) {
      await Promise.all(frontKinds.map((k) => this.fetchCode(k)));
    },
  },
});
